/**
 * Importimi i stafit nga një fletë e jashtme (Excel/CSV).
 *
 * Klienti që vjen nga një sistem tjetër e ka listën e vet: kolona me emra që
 * s'i njeh askush këtu ("Anual Leave", "branch", "Contract Start Date"), data
 * në tri formate në të njëjtën kolonë, dhe departamente të shkruara si tekst.
 * Nëse ai skedar do t'i dorëzohej drejt e API-së, gjysma e rreshtave do të
 * binte me 400 dhe askush s'do ta merrte vesh cili rresht e pse.
 *
 * Prandaj gjithçka që vendos — cila kolonë është cila, çfarë do të thotë
 * "Female", a është "15/8/2025" gusht apo maj — rri KËTU, në funksione të
 * pastra pa React dhe pa rrjet. Dialogu vetëm i vizaton. Rregullat mbahen nga
 * `staffImport.test.js`.
 *
 * Rregulli i vetëm i pathyeshëm: asnjë rresht nuk shkon te serveri pa u parë
 * nga njeriu. Importi është veprim që s'kthehet me një klik.
 */
import * as XLSX from 'xlsx-js-style';
import {
  normKey, parseDateCell, parseBool, parseNumber, ymd,
  detectMappingFor, unmappedHeaders, readWorkbookWith,
} from './importCells';

// Leximi i një qelize të huaj — datë, numër, Po/Jo, koka e kolonës — nuk i
// takon stafit: i takon Excel-it. Rri te `importCells.js` dhe e ndajnë të
// gjitha importet. Ri-eksportohen që kjo derë të mbetet e njëjta për atë që
// e përdorte tashmë `staffImport`.
export { normKey, parseDateCell, parseBool, parseNumber, unmappedHeaders };

const HEX = { ink: '111827', muted: '6B7280', rule: 'E2E8F0', brand: '4893A5', navy: '303D50', soft: 'F8FAFC', tint: 'EDF4F6', req: 'FEF3C7' };

// ── Parsues ──────────────────────────────────────────────────────────────────

const MALE = new Set(['m', 'male', 'mashkull', 'burre', 'mann', 'mannlich', 'maschio']);
const FEMALE = new Set(['f', 'female', 'femer', 'femra', 'grua', 'vajze', 'weiblich', 'frau', 'w']);

/** Gjinia → 'M' | 'F' | null. Kolona në bazë është CHAR(1). */
export function parseGender(v) {
  const k = normKey(v);
  if (!k) return null;
  if (FEMALE.has(k)) return 'F';
  if (MALE.has(k)) return 'M';
  return null;
}

const NET = new Set(['net', 'neto', 'netto', 'nett', 'n']);
const GROSS = new Set(['gross', 'bruto', 'brutto', 'brut', 'b', 'g']);

/**
 * Baza e pagës → 'gross' | 'net' | null.
 *
 * Kolona më e rrezikshme e gjithë fletës. Pa të çdo shifër lexohet si bruto,
 * dhe te një klient që i mban pagat neto e gjithë lista del gabim — pa asnjë
 * gabim në ekran, sepse numri i shfaqur është pikërisht ai që u importua.
 * Prandaj vlera e panjohur kthen `null` dhe ekrani e shënon, në vend që të
 * zgjedhë në heshtje.
 */
export function parseSalaryBasis(v) {
  const k = normKey(v);
  if (!k) return null;
  if (NET.has(k)) return 'net';
  if (GROSS.has(k)) return 'gross';
  return null;
}

const FOREIGN = new Set(['foreign', 'i huaj', 'e huaj', 'huaj', 'jorezident', 'jo rezident', 'non resident', 'nonresident', 'auslander', 'ausland']);
const RESIDENT = new Set(['resident', 'rezident', 'vendas', 'vendor', 'domestic', 'inlander']);

/** Rezidenca tatimore → 'resident' | 'foreign' | null. */
export function parseResidency(v) {
  const k = normKey(v);
  if (!k) return null;
  if (FOREIGN.has(k)) return 'foreign';
  if (RESIDENT.has(k)) return 'resident';
  return null;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/** Telefoni mbahet si tekst: "044 641 818" dhe "+383 45 872 390" janë të dyja të vlefshme. */
export function cleanPhone(v) {
  if (v === null || v === undefined) return '';
  // Excel-i e kthen shpesh numrin e telefonit në numër dhe e humb zeron e parë;
  // ai dëm ndodh para nesh, por të paktën mos e shtojmë notacionin shkencor.
  if (typeof v === 'number') return Number.isInteger(v) ? String(v) : String(v);
  return String(v).trim();
}

// ── Seksionet ────────────────────────────────────────────────────────────────
// Dyzet kolona në një rresht të vetëm janë të palexueshme. Seksionet e ndajnë
// fletën dhe hartën në grupe që i njeh njeriu i HR-së: identiteti, kontakti,
// vendi i punës, kontrata, tatimi, ligji, sigurimi, pushimet, largimi.
export const SECTIONS = [
  { key: 'identity',  label: 'Identiteti' },
  { key: 'personal',  label: 'Të dhënat personale' },
  { key: 'contact',   label: 'Kontakti' },
  { key: 'job',       label: 'Vendi i punës' },
  { key: 'contract',  label: 'Kontrata dhe paga' },
  { key: 'tax',       label: 'Tatimi dhe pagesa' },
  { key: 'legal',     label: 'Detyrimet ligjore' },
  { key: 'insurance', label: 'Sigurimi shëndetësor' },
  { key: 'leave',     label: 'Pushimet' },
  { key: 'exit',      label: 'Largimi' },
  { key: 'other',     label: 'Tjetër' },
];

// ── Regjistri i kolonave ─────────────────────────────────────────────────────
// Një listë e vetme e ushqen shabllonin, njohjen automatike të kolonave,
// hartën e redaktueshme, validimin dhe ngarkesën për API-në. Kolonë e re →
// një zë këtu, asgjë tjetër.
//
// Rendi është ai i rëndësisë: e detyrueshmja e para, pastaj ajo pa të cilën
// punëtori nuk llogaritet drejt (paga bruto/neto, rezidenca, punësimi i dytë),
// pastaj detyrimet ligjore, dhe në fund ajo që mund të mbetet bosh pa pasojë.
export const IMPORT_COLUMNS = [
  // ── Identiteti ────────────────────────────────────────────────────────────
  {
    key: 'FirstName', label: 'Emri', section: 'identity', required: true, type: 'text', width: 16,
    example: 'Dorentina',
    aliases: ['emri', 'name', 'first name', 'firstname', 'given name', 'vorname', 'emri i punetorit', 'emri i punonjesit'],
  },
  {
    key: 'LastName', label: 'Mbiemri', section: 'identity', required: true, type: 'text', width: 16,
    example: 'Qenaj',
    aliases: ['mbiemri', 'surname', 'last name', 'lastname', 'family name', 'nachname'],
  },

  // ── Të dhënat personale ───────────────────────────────────────────────────
  {
    key: 'PersonalNumber', label: 'Numri personal', section: 'personal', type: 'text', width: 16, unique: true,
    example: '1240891138',
    aliases: ['numri personal', 'nr personal', 'id', 'personal number', 'personal no', 'personalnummer', 'nid', 'numri i leternjoftimit', 'ssn'],
  },
  {
    // Klienti vjen shpesh me kodet e veta të punëtorëve, dhe ato jetojnë te
    // dokumentet e tij të vjetra. Kur kolona është bosh, sistemi e gjeneron
    // vetë (EMP-00042) — por një kod i sjellë nuk mbishkruhet.
    key: 'EmployeeCode', label: 'Kodi i punëtorit', section: 'personal', type: 'text', width: 14, unique: true,
    example: '',
    aliases: ['kodi i punetorit', 'kodi', 'employee code', 'employee no', 'emp code', 'staff id', 'personalnummer intern', 'badge'],
  },
  {
    key: 'DateOfBirth', label: 'Datëlindja', section: 'personal', type: 'date', width: 14,
    example: '1998-11-24',
    aliases: ['datelindja', 'data e lindjes', 'date of birth', 'birth date', 'dob', 'geburtsdatum'],
  },
  {
    key: 'Gender', label: 'Gjinia', section: 'personal', type: 'gender', width: 10,
    example: 'Femër',
    aliases: ['gjinia', 'gender', 'sex', 'geschlecht'],
  },

  // ── Kontakti ──────────────────────────────────────────────────────────────
  {
    key: 'Email', label: 'Email', section: 'contact', type: 'text', width: 28, unique: true,
    example: 'dorentina.qenaj@example.com',
    aliases: ['email', 'e mail', 'mail', 'adresa e emailit', 'email address', 'posta elektronike'],
  },
  {
    key: 'Phone', label: 'Telefoni', section: 'contact', type: 'phone', width: 16,
    example: '049 811 420',
    aliases: ['telefoni', 'tel', 'phone', 'phone number', 'mobile', 'numri i telefonit', 'telefon', 'nr tel'],
  },
  {
    key: 'Address', label: 'Adresa', section: 'contact', type: 'text', width: 26,
    example: 'Rr. Agim Ramadani 12',
    aliases: ['adresa', 'address', 'street', 'adresse', 'vendbanimi'],
  },
  {
    // Nuk ka kolonë "qytet" te punëtori — i bashkohet adresës te `validateRows`.
    key: 'City', label: 'Qyteti', section: 'contact', type: 'text', width: 14,
    example: 'Prishtinë',
    aliases: ['qyteti', 'city', 'town', 'stadt', 'komuna'],
  },

  // ── Vendi i punës ─────────────────────────────────────────────────────────
  {
    key: 'HireDate', label: 'Data e fillimit', section: 'job', type: 'date', width: 14,
    example: '2020-11-02',
    aliases: ['data e fillimit', 'data e punesimit', 'start date', 'hire date', 'employment date', 'eintrittsdatum', 'data e fillimit te punes'],
  },
  {
    key: 'Position', label: 'Pozita', section: 'job', type: 'text', width: 22,
    example: 'Menaxhere e Operacioneve',
    aliases: ['pozita', 'position', 'job title', 'title', 'role', 'posten', 'vendi i punes', 'pozicioni'],
  },
  {
    key: 'Department', label: 'Departamenti', section: 'job', type: 'lookup', width: 20, lookup: 'departments',
    example: 'Menaxhment',
    aliases: ['departamenti', 'department', 'dept', 'abteilung', 'sektori'],
  },
  {
    key: 'Branch', label: 'Dega', section: 'job', type: 'lookup', width: 18, lookup: 'branches',
    example: 'Dega Prishtinë',
    aliases: ['dega', 'branch', 'office', 'location', 'filiale', 'njesia'],
  },
  {
    key: 'Supervisor', label: 'Mbikëqyrësi', section: 'job', type: 'lookup', width: 20, lookup: 'workers',
    example: 'Art Safqi',
    aliases: ['mbikeqyresi', 'supervisor', 'manager', 'eprori', 'reports to', 'vorgesetzter'],
  },
  {
    key: 'Active', label: 'Statusi', section: 'job', type: 'bool', width: 12,
    example: 'Aktiv',
    aliases: ['statusi', 'status', 'active', 'gjendja', 'employment status'],
  },

  // ── Kontrata dhe paga ─────────────────────────────────────────────────────
  {
    key: 'ContractType', label: 'Lloji i kontratës', section: 'contract', type: 'text', width: 18,
    example: 'Full time',
    aliases: ['lloji i kontrates', 'contract type', 'lloji i punesimit', 'employment type', 'vertragsart', 'tipi i kontrates'],
  },
  {
    key: 'ContractStart', label: 'Data e kontratës', section: 'contract', type: 'date', width: 15,
    example: '2025-12-31',
    aliases: ['data e kontrates', 'contract start date', 'contract start', 'contract date', 'vertragsbeginn', 'fillimi i kontrates'],
  },
  {
    // Kontrata me afat pa datë mbarimi është kontratë e pacaktuar — dallim që
    // e ndryshon njoftimin e skadimit, ndërprerjen dhe raportet e compliance-it.
    key: 'ContractEnd', label: 'Data e mbarimit të kontratës', section: 'contract', type: 'date', width: 22,
    example: '',
    aliases: ['data e mbarimit te kontrates', 'contract end date', 'contract end', 'end date', 'skadimi i kontrates', 'vertragsende', 'mbarimi i kontrates'],
  },
  {
    key: 'Salary', label: 'Paga', section: 'contract', type: 'number', width: 12,
    example: '650',
    aliases: ['paga', 'paga bruto', 'salary', 'gross salary', 'bruto', 'gehalt', 'rroga', 'paga mujore'],
  },
  {
    // Pa këtë kolonë çdo pagë lexohet si BRUTO. Te një klient që i mban pagat
    // neto, e gjithë lista e pagave del gabim — dhe gabimi nuk duket, sepse
    // shifra në ekran është pikërisht ajo që u importua.
    key: 'SalaryType', label: 'Paga është bruto apo neto', section: 'contract', type: 'salaryType', width: 22,
    example: 'Bruto',
    aliases: ['paga eshte bruto apo neto', 'bruto neto', 'brutoneto', 'lloji i pages', 'salary type', 'gross or net', 'basis', 'baza'],
  },
  {
    key: 'HourSalary', label: 'Paga në orë', section: 'contract', type: 'number', width: 13,
    example: '',
    aliases: ['paga ne ore', 'cmimi i ores', 'hourly rate', 'hour salary', 'rate per hour', 'stundenlohn'],
  },

  // ── Tatimi dhe pagesa ─────────────────────────────────────────────────────
  {
    // 'resident' / 'foreign' — ndryshon përllogaritjen e tatimit te paga.
    key: 'Residency', label: 'Rezidenca', section: 'tax', type: 'residency', width: 14,
    example: 'Rezident',
    aliases: ['rezidenca', 'residency', 'resident', 'rezident', 'shtetesia tatimore', 'tax residency', 'i huaj'],
  },
  {
    // Punësimi i dytë e heq lirimin tatimor bazë — pa këtë fushë punëtori
    // paguan më pak tatim se sa duhet, dhe diferencën e gjen ATK-ja.
    key: 'SecondJob', label: 'Punësim i dytë', section: 'tax', type: 'bool', width: 15,
    example: 'Jo',
    aliases: ['punesim i dyte', 'punesimi i dyte', 'second job', 'secondary employment', 'punedhenes i dyte', 'zweitjob'],
  },
  {
    key: 'BankAccount', label: 'Llogaria bankare', section: 'tax', type: 'text', width: 24,
    example: '',
    aliases: ['llogaria bankare', 'llogaria', 'iban', 'bank account', 'account number', 'numri i llogarise', 'bankkonto'],
  },
  {
    key: 'PensionNumber', label: 'Numri i trustit', section: 'tax', type: 'text', width: 16,
    example: '',
    aliases: ['numri i trustit', 'trusti', 'pension number', 'pension', 'numri i pensionit', 'rentennummer'],
  },

  // ── Detyrimet ligjore ─────────────────────────────────────────────────────
  {
    key: 'ATKRegistered', label: 'E regjistruar në ATK', section: 'legal', type: 'bool', width: 18,
    example: 'Po',
    aliases: ['e regjistruar ne atk', 'atk', 'atk registered', 'i regjistruar ne atk', 'regjistrimi ne atk', 'tax registered'],
  },
  {
    key: 'ATKRegisteredAt', label: 'Data e regjistrimit në ATK', section: 'legal', type: 'date', width: 22,
    example: '',
    aliases: ['data e regjistrimit ne atk', 'atk date', 'atk registration date', 'data e atk'],
  },
  {
    key: 'WorkPermitNumber', label: 'Leja e punës — numri', section: 'legal', type: 'text', width: 18,
    example: '',
    aliases: ['leja e punes numri', 'leja e punes', 'work permit', 'work permit number', 'permit no', 'arbeitserlaubnis'],
  },
  {
    key: 'WorkPermitIssued', label: 'Leja e punës — lëshuar', section: 'legal', type: 'date', width: 20,
    example: '',
    aliases: ['leja e punes leshuar', 'work permit issued', 'permit issued', 'data e leshimit te lejes'],
  },
  {
    key: 'WorkPermitExpires', label: 'Leja e punës — skadon', section: 'legal', type: 'date', width: 20,
    example: '',
    aliases: ['leja e punes skadon', 'work permit expires', 'permit expiry', 'skadimi i lejes se punes'],
  },

  // ── Sigurimi shëndetësor ──────────────────────────────────────────────────
  {
    // Aliasi i zhveshur «Insurance» MUNGON me qëllim. Fleta e klientit e ka
    // atë kolonë me Po/Jo, jo me numër kartele; po ta pranonim, te numri i
    // sigurimit të çdo punëtori do të shkruhej fjala «Yes». Kolona mbetet e
    // palidhur dhe njeriu e harton vetë nëse te fleta e tij është vërtet numri.
    key: 'HealthInsNumber', label: 'Sigurimi — numri i kartelës', section: 'insurance', type: 'text', width: 22,
    example: '',
    aliases: ['sigurimi numri i karteles', 'numri i sigurimit', 'health insurance number', 'insurance number', 'nr i sigurimit', 'versicherungsnummer'],
  },
  {
    key: 'HealthInsPersonal', label: 'Sigurimi — shuma personale (€)', section: 'insurance', type: 'number', width: 24,
    example: '',
    aliases: ['sigurimi shuma personale', 'sigurimi personal', 'health insurance amount', 'insurance premium', 'shuma e sigurimit'],
  },
  {
    key: 'HealthInsExpires', label: 'Sigurimi — skadon', section: 'insurance', type: 'date', width: 18,
    example: '',
    aliases: ['sigurimi skadon', 'insurance expires', 'insurance expiry', 'skadimi i sigurimit', 'health insurance expiry'],
  },

  // ── Pushimet ──────────────────────────────────────────────────────────────
  {
    key: 'AnnualLeave', label: 'Pushimi vjetor (ditë)', section: 'leave', type: 'number', width: 18,
    example: '20',
    aliases: ['pushimi vjetor', 'pushimi vjetor dite', 'anual leave', 'annual leave', 'vacation days', 'urlaub', 'pushimi vjetor i mbetur'],
  },
  {
    key: 'SickLeave', label: 'Pushimi mjekësor (ditë)', section: 'leave', type: 'number', width: 20,
    example: '18',
    aliases: ['pushimi mjekesor', 'pushimi mjekesor dite', 'sick leave', 'sick leave available', 'krankheitstage', 'pushim mjekesor i mbetur'],
  },
  {
    key: 'LeaveYear', label: 'Viti i bilancit', section: 'leave', type: 'number', width: 13,
    example: '2026',
    aliases: ['viti i bilancit', 'viti', 'year', 'jahr', 'leave year', 'balance year'],
  },

  // ── Largimi ───────────────────────────────────────────────────────────────
  {
    // Një ish-punëtor i importuar si «joaktiv» pa datë largimi nuk del te
    // asnjë raport qarkullimi — largimi ekziston, por pa moment.
    key: 'EmploymentEndDate', label: 'Data e largimit', section: 'exit', type: 'date', width: 16,
    example: '',
    aliases: ['data e largimit', 'data e nderprerjes', 'termination date', 'end of employment', 'leaving date', 'austrittsdatum'],
  },
  {
    key: 'SeparationReason', label: 'Arsyeja e largimit', section: 'exit', type: 'text', width: 26,
    example: '',
    aliases: ['arsyeja e largimit', 'arsyeja', 'termination reason', 'reason for leaving', 'separation reason', 'kundigungsgrund'],
  },

  // ── Tjetër ────────────────────────────────────────────────────────────────
  {
    key: 'Notes', label: 'Shënime', section: 'other', type: 'text', width: 30,
    example: '',
    aliases: ['shenime', 'notes', 'note', 'comment', 'koment', 'bemerkung', 'remarks'],
  },
];

export const COLUMN_BY_KEY = IMPORT_COLUMNS.reduce((m, c) => { m[c.key] = c; return m; }, {});

// Sfondi i kokës te shablloni. Seksionet alternojnë mes dy tonesh neutrale —
// jo njëmbëdhjetë ngjyra: ngjyra këtu shënon KUFIRIN e grupit, dhe një ylber
// do të thoshte se secili grup ka kuptim të ndryshëm, çka nuk e ka.
const SECTION_FILL = SECTIONS.reduce((m, s, i) => { m[s.key] = i % 2 ? HEX.tint : HEX.soft; return m; }, {});

// ── Njohja e kolonave dhe leximi i skedarit ──────────────────────────────────
// Të dyja rrinë te `importCells.js`: algoritmi i njohjes (dy kalime, përputhje
// me fjalë) nuk ka asgjë prej stafi, dhe importi i aseteve e do të njëjtin —
// një kopje e dytë do të ndahej prej kësaj te rregullimi i parë.

/** Kokat e skedarit → `{ [columnKey]: headerIndex }` (−1 = e palidhur). */
export const detectMapping = (headers) => detectMappingFor(IMPORT_COLUMNS, headers);

/** ArrayBuffer (xlsx/xls/csv) → `{ headers, rows, sheetName, sheetNames }`. */
export const readWorkbook = (data, sheetName) => readWorkbookWith(XLSX, data, sheetName);

// ── Validimi ─────────────────────────────────────────────────────────────────

const MSG = {
  required: (label) => `${label} është e detyrueshme`,
  badDate: (label) => `${label}: data nuk lexohet`,
  futureDate: (label) => `${label}: data është në të ardhmen`,
  ambiguousDate: (label) => `${label}: ditë/muaj të paqarta — u lexua si ditë/muaj`,
  badEmail: 'Email-i nuk është i vlefshëm',
  dupInFile: (label) => `${label} përsëritet brenda skedarit`,
  dupInDb: (label) => `${label} ekziston tashmë te sistemi`,
  existingBalanceOnly: 'Punëtori ekziston — nuk krijohet sërish; përditësohet vetëm bilanci i pushimeve',
  badGender: 'Gjinia nuk njihet — lëre bosh ose shkruaj M/F',
  badBool: 'Statusi nuk njihet — u supozua «aktiv»',
  noMatch: (label, v) => `${label} «${v}» nuk u gjet — rreshti ruhet pa të`,
  badNumber: (label) => `${label}: nuk është numër`,
  impossibleAge: 'Datëlindja jep moshë të pamundur',
  contractFromHire: 'Data e kontratës mungon — u përdor data e punësimit, që paga të mos humbasë',
  salaryLost: 'Pa datë kontrate dhe pa datë punësimi nuk krijohet kontratë — PAGA NUK RUHET. Plotëso njërën nga dy datat.',
  endBeforeStart: 'Kontrata mbaron para se të nisë — data e mbarimit u lëshua',
  leaveNoYear: 'Bilanci i pushimeve pa vit — u përdor viti aktual',
  badBasis: 'Bruto apo neto nuk u kuptua — u ruajt si BRUTO. Kontrolloje: një pagë neto e futur si bruto e nxjerr tërë listën gabim.',
  badResidency: 'Rezidenca nuk u kuptua — u ruajt «rezident»',
  unknownYesNo: (label) => `${label}: vlera nuk njihet — u ruajt «jo»`,
  inactiveNoEnd: 'Punëtor joaktiv pa datë largimi — nuk do të dalë te raportet e qarkullimit',
};

const lookupIndex = (list, fields) => {
  const idx = new Map();
  (list || []).forEach((item) => {
    fields.forEach((f) => {
      const v = normKey(typeof f === 'function' ? f(item) : item[f]);
      if (v && !idx.has(v)) idx.set(v, item);
    });
  });
  return idx;
};

/**
 * Rreshtat e papërpunuar → rreshta të gatshëm, me gabime dhe paralajmërime.
 *
 * Dallimi mban gjithë peshën: **gabimi** e ndalon rreshtin (pa emër, email i
 * dyfishtë), **paralajmërimi** e lejon por e thotë çfarë humbi (departament që
 * s'u gjet, gjini e panjohur). Ekrani i lejon të dyja të shihen para se të
 * shtypet «Importo» — dhe vetëm rreshtat pa gabime dërgohen.
 */
export function validateRows(rows, mapping, ctx = {}) {
  const {
    departments = [], branches = [], workers = [],
    defaultYear = new Date().getFullYear(),
  } = ctx;

  const deptIdx = lookupIndex(departments, ['Name', 'name']);
  const branchIdx = lookupIndex(branches, ['Name', 'name']);
  const workerIdx = lookupIndex(workers, [
    (w) => `${w.FirstName || ''} ${w.LastName || ''}`,
    'Email', 'PersonalNumber',
  ]);
  const dbEmails = new Set((workers || []).map((w) => normKey(w.Email)).filter(Boolean));
  const dbPersonal = new Set((workers || []).map((w) => normKey(w.PersonalNumber)).filter(Boolean));

  const dbCodes = new Set((workers || []).map((w) => normKey(w.EmployeeCode)).filter(Boolean));

  const seenEmail = new Map();
  const seenPersonal = new Map();
  const seenCode = new Map();

  const cell = (row, key) => {
    const i = mapping?.[key];
    return i === undefined || i < 0 ? '' : row[i];
  };

  const out = rows.map((row, i) => {
    const errors = [];
    const warnings = [];
    const values = {};
    const add = (list, key, message) => list.push({ key, message });

    // ── Identiteti ────────────────────────────────────────────────────────
    for (const key of ['FirstName', 'LastName']) {
      const v = String(cell(row, key) ?? '').trim();
      values[key] = v;
      if (!v) add(errors, key, MSG.required(COLUMN_BY_KEY[key].label));
    }

    const personal = String(cell(row, 'PersonalNumber') ?? '').trim();
    values.PersonalNumber = personal;
    if (personal) {
      const k = normKey(personal);
      if (seenPersonal.has(k)) add(errors, 'PersonalNumber', `${MSG.dupInFile('Numri personal')} (rreshti ${seenPersonal.get(k) + 1})`);
      else if (dbPersonal.has(k)) add(errors, 'PersonalNumber', MSG.dupInDb('Numri personal'));
      else seenPersonal.set(k, i);
    }

    const email = String(cell(row, 'Email') ?? '').trim();
    values.Email = email;
    if (email) {
      if (!EMAIL_RE.test(email)) add(errors, 'Email', MSG.badEmail);
      else {
        const k = normKey(email);
        if (seenEmail.has(k)) add(errors, 'Email', `${MSG.dupInFile('Email-i')} (rreshti ${seenEmail.get(k) + 1})`);
        else if (dbEmails.has(k)) add(errors, 'Email', MSG.dupInDb('Email-i'));
        else seenEmail.set(k, i);
      }
    }

    const code = String(cell(row, 'EmployeeCode') ?? '').trim();
    values.EmployeeCode = code;
    if (code) {
      const k = normKey(code);
      if (seenCode.has(k)) add(errors, 'EmployeeCode', `${MSG.dupInFile('Kodi i punëtorit')} (rreshti ${seenCode.get(k) + 1})`);
      else if (dbCodes.has(k)) add(errors, 'EmployeeCode', MSG.dupInDb('Kodi i punëtorit'));
      else seenCode.set(k, i);
    }

    // ── Datat ─────────────────────────────────────────────────────────────
    const today = ymd(new Date().getFullYear(), new Date().getMonth() + 1, new Date().getDate());
    for (const key of [
      'DateOfBirth', 'HireDate', 'ContractStart', 'ContractEnd', 'ATKRegisteredAt',
      'WorkPermitIssued', 'WorkPermitExpires', 'HealthInsExpires', 'EmploymentEndDate',
    ]) {
      const raw = cell(row, key);
      if (raw === '' || raw === null || raw === undefined) { values[key] = null; continue; }
      const { value, ambiguous } = parseDateCell(raw);
      values[key] = value;
      const label = COLUMN_BY_KEY[key].label;
      if (!value) { add(warnings, key, MSG.badDate(label)); continue; }
      if (ambiguous) add(warnings, key, MSG.ambiguousDate(label));
      if (key === 'DateOfBirth') {
        const age = Number(today.slice(0, 4)) - Number(value.slice(0, 4));
        if (value > today || age > 100 || age < 14) { values[key] = null; add(warnings, key, MSG.impossibleAge); }
      }
    }

    // ── Kontakti dhe vendi ────────────────────────────────────────────────
    values.Phone = cleanPhone(cell(row, 'Phone'));
    const address = String(cell(row, 'Address') ?? '').trim();
    const city = String(cell(row, 'City') ?? '').trim();
    values.Address = [address, city].filter(Boolean).join(', ');
    values.City = city;
    values.Position = String(cell(row, 'Position') ?? '').trim();
    values.Notes = String(cell(row, 'Notes') ?? '').trim();
    for (const key of ['BankAccount', 'PensionNumber', 'WorkPermitNumber', 'HealthInsNumber', 'SeparationReason']) {
      values[key] = String(cell(row, key) ?? '').trim();
    }

    // ── Gjinia dhe statusi ────────────────────────────────────────────────
    const rawGender = cell(row, 'Gender');
    values.Gender = parseGender(rawGender);
    if (String(rawGender ?? '').trim() && !values.Gender) add(warnings, 'Gender', MSG.badGender);

    const rawActive = cell(row, 'Active');
    const activeParsed = parseBool(rawActive);
    values.Active = activeParsed === null ? true : activeParsed;
    if (String(rawActive ?? '').trim() && activeParsed === null) add(warnings, 'Active', MSG.badBool);

    // Po/Jo të tjera: mungesa këtu do të thotë «jo», jo «nuk e dimë» — kolonat
    // ekzistuese te baza janë `false` si parazgjedhje.
    for (const key of ['SecondJob', 'ATKRegistered']) {
      const raw = cell(row, key);
      const parsed = parseBool(raw);
      values[key] = parsed === null ? false : parsed;
      if (String(raw ?? '').trim() && parsed === null) {
        add(warnings, key, MSG.unknownYesNo(COLUMN_BY_KEY[key].label));
      }
    }

    // ── Lidhjet me organizatën ────────────────────────────────────────────
    const resolve = (key, idx, list) => {
      const raw = String(cell(row, key) ?? '').trim();
      values[`${key}Text`] = raw;
      if (!raw) { values[key] = null; return; }
      const hit = idx.get(normKey(raw));
      if (hit) { values[key] = hit.Id ?? hit.id ?? null; return; }
      values[key] = null;
      add(warnings, key, MSG.noMatch(COLUMN_BY_KEY[key].label, raw));
      if (list) list.add(raw);
    };
    resolve('Department', deptIdx, ctx.missingDepartments);
    resolve('Branch', branchIdx, ctx.missingBranches);
    resolve('Supervisor', workerIdx, null);

    // ── Paga: bruto apo neto ──────────────────────────────────────────────
    // Vlera e panjohur NUK zgjidhet në heshtje. Sistemi e ruan si 'gross'
    // (parazgjedhja e kolonës te baza), por rreshti shënohet — përndryshe një
    // fletë me paga neto do të hynte e tëra si bruto pa asnjë shenjë.
    const rawBasis = cell(row, 'SalaryType');
    const basis = parseSalaryBasis(rawBasis);
    values.SalaryType = basis || 'gross';
    if (String(rawBasis ?? '').trim() && !basis) add(warnings, 'SalaryType', MSG.badBasis);

    const rawResidency = cell(row, 'Residency');
    const residency = parseResidency(rawResidency);
    values.Residency = residency || 'resident';
    if (String(rawResidency ?? '').trim() && !residency) add(warnings, 'Residency', MSG.badResidency);

    // ── Kontrata ──────────────────────────────────────────────────────────
    values.ContractType = String(cell(row, 'ContractType') ?? '').trim();
    for (const key of ['Salary', 'HourSalary', 'HealthInsPersonal']) {
      const raw = cell(row, key);
      const n = parseNumber(raw);
      values[key] = n;
      if (String(raw ?? '').trim() && n === null) add(warnings, key, MSG.badNumber(COLUMN_BY_KEY[key].label));
    }
    // Paga jeton te KONTRATA, jo te punëtori: pa kontratë ajo nuk ruhet askund.
    // Më parë mjaftonte një «Data e kontratës» bosh që e gjithë kolona e pagave
    // të zhdukej — ekrani paralajmëronte vetëm se kontrata s'krijohet, kurrë se
    // paga humbet. Kur mungon vetëm ajo datë, kontrata nis nga data e punësimit.
    const hasContractData = Boolean(values.ContractType) || values.Salary !== null || values.HourSalary !== null;
    values.ContractStartUsed = values.ContractStart || (hasContractData ? values.HireDate : null);
    values.hasContract = Boolean(values.ContractStartUsed);
    if (!values.ContractStart && hasContractData) {
      add(warnings, 'ContractStart', values.HireDate ? MSG.contractFromHire : MSG.salaryLost);
    }
    // Kontrata që mbaron para se të nisë nuk krijohet: do të dukej e skaduar
    // që nga dita e parë dhe do të hynte te njoftimet e skadimit menjëherë.
    if (values.ContractEnd && values.ContractStart && values.ContractEnd < values.ContractStart) {
      values.ContractEnd = null;
      add(warnings, 'ContractEnd', MSG.endBeforeStart);
    }

    // ── Largimi ───────────────────────────────────────────────────────────
    // Një punëtor i shënuar joaktiv pa datë largimi nuk del te asnjë raport
    // qarkullimi — largimi ekziston, por pa moment.
    if (!values.Active && !values.EmploymentEndDate) {
      add(warnings, 'EmploymentEndDate', MSG.inactiveNoEnd);
    }

    // ── Bilanci i pushimeve ───────────────────────────────────────────────
    for (const key of ['AnnualLeave', 'SickLeave']) {
      const raw = cell(row, key);
      const n = parseNumber(raw);
      values[key] = n;
      if (String(raw ?? '').trim() && n === null) add(warnings, key, MSG.badNumber(COLUMN_BY_KEY[key].label));
      else if (n !== null && (n < 0 || n > 365)) { values[key] = null; add(warnings, key, MSG.badNumber(COLUMN_BY_KEY[key].label)); }
    }
    const year = parseNumber(cell(row, 'LeaveYear'));
    values.LeaveYear = year && year >= 2000 && year <= 2100 ? Math.round(year) : defaultYear;
    if ((values.AnnualLeave !== null || values.SickLeave !== null) && !year) {
      add(warnings, 'LeaveYear', MSG.leaveNoYear);
    }

    // Punëtori ekziston, por rreshti sjell ditë pushimi: s'është gabim. Serveri
    // s'e krijon sërish punëtorin, vetëm ia vendos bilancin — pa këtë, ringarkimi
    // i listës për stafin ekzistues nuk sillte asnjë ditë pushimi.
    const DUP = [MSG.dupInDb('Numri personal'), MSG.dupInDb('Email-i'), MSG.dupInDb('Kodi i punëtorit')];
    const hasLeave = values.AnnualLeave !== null || values.SickLeave !== null;
    const dbDup = errors.filter((e) => DUP.includes(e.message));
    let existing = false;
    if (hasLeave && dbDup.length && dbDup.length === errors.length
        && dbDup.some((e) => e.key === 'PersonalNumber' || e.key === 'Email')) {
      errors.length = 0;
      add(warnings, dbDup[0].key, MSG.existingBalanceOnly);
      existing = true;
    }

    return { index: i, raw: row, values, errors, warnings, ok: errors.length === 0, existing };
  });

  return out;
}

/** Rreshti i validuar → trupi që pret `/api/workers/import`. */
export function toPayload(row) {
  const v = row.values;
  return {
    row: row.index + 1,
    FirstName: v.FirstName,
    LastName: v.LastName,
    PersonalNumber: v.PersonalNumber || null,
    DateOfBirth: v.DateOfBirth,
    Gender: v.Gender,
    Email: v.Email || null,
    Phone: v.Phone || null,
    Address: v.Address || null,
    HireDate: v.HireDate,
    Position: v.Position || null,
    Department: v.Department,
    Branch: v.Branch,
    SupervisorId: v.Supervisor,
    // Emri shkon bashkë me id-në: kur eprori vjen në TË NJËJTËN fletë, ai ende
    // nuk ekziston kur validohet rreshti — serveri e lidh pasi t'i ketë futur të gjithë.
    SupervisorName: v.SupervisorText || null,
    Active: v.Active,
    Notes: v.Notes || null,
    // Kodi i klientit ruhet siç erdhi; bosh do të thotë «gjenerojë sistemi».
    EmployeeCode: v.EmployeeCode || null,

    // ── Tatimi dhe pagesa ────────────────────────────────────────────────
    SalaryType: v.SalaryType,
    Residency: v.Residency,
    SecondJob: v.SecondJob,
    BankAccount: v.BankAccount || null,
    PensionNumber: v.PensionNumber || null,

    // ── Detyrimet ligjore ────────────────────────────────────────────────
    ATKRegistered: v.ATKRegistered,
    ATKRegisteredAt: v.ATKRegisteredAt,
    WorkPermitNumber: v.WorkPermitNumber || null,
    WorkPermitIssued: v.WorkPermitIssued,
    WorkPermitExpires: v.WorkPermitExpires,

    // ── Sigurimi shëndetësor ─────────────────────────────────────────────
    HealthInsNumber: v.HealthInsNumber || null,
    HealthInsPersonal: v.HealthInsPersonal,
    HealthInsExpires: v.HealthInsExpires,

    // ── Largimi ──────────────────────────────────────────────────────────
    EmploymentEndDate: v.EmploymentEndDate,
    SeparationReason: v.SeparationReason || null,

    Contract: v.hasContract
      ? {
        StartDate: v.ContractStartUsed || v.ContractStart,
        EndDate: v.ContractEnd,
        Type: v.ContractType || null,
        Salary: v.Salary ?? 0,
        HourSalary: v.HourSalary,
        Position: v.Position || null,
      }
      : null,
    LeaveBalances: [
      v.AnnualLeave !== null && v.AnnualLeave !== undefined ? { Code: 'annual', Days: v.AnnualLeave } : null,
      // `sick` — kodi i vërtetë te `leave_types`. Deri tani dërgohej 'medical',
      // një kod që s'ekziston askund: serveri s'e gjente dot dhe ditët binin.
      v.SickLeave !== null && v.SickLeave !== undefined ? { Code: 'sick', Days: v.SickLeave } : null,
    ].filter(Boolean),
    LeaveYear: v.LeaveYear,
  };
}

/** Përmbledhja që rri mbi tabelën e parashikimit. */
export function summarize(rows) {
  return {
    total: rows.length,
    ready: rows.filter((r) => r.ok && r.warnings.length === 0).length,
    warned: rows.filter((r) => r.ok && r.warnings.length > 0).length,
    blocked: rows.filter((r) => !r.ok).length,
    contracts: rows.filter((r) => r.ok && r.values.hasContract).length,
    balances: rows.filter((r) => r.ok && (r.values.AnnualLeave !== null || r.values.SickLeave !== null)).length,
  };
}

// ── Shablloni ────────────────────────────────────────────────────────────────

const line = (rgb, side = 'bottom', style = 'thin') => ({ [side]: { style, color: { rgb } } });

/**
 * Shablloni `.xlsx` që shkarkon klienti: fleta e të dhënave me dy rreshta
 * shembull, plus një fletë udhëzimesh që i thotë emrat e sakta të
 * departamenteve dhe degëve që ekzistojnë tashmë.
 *
 * Emrat e departamenteve dalin nga sistemi, nuk shkruhen me dorë: pikërisht
 * mospërputhja e tyre («Operations» kundrejt «Operacione») është arsyeja e
 * zakonshme pse gjysma e rreshtave vjen pa departament.
 */
// Rreshti i dytë shembull — një punëtor me profil tjetër nga i pari (me afat,
// me pagë neto, i huaj me leje pune), që fleta të tregojë edhe kolonat që
// klienti do t'i shpërfillte po t'i shihte gjithmonë bosh.
const SECOND_EXAMPLE = {
  FirstName: 'Art', LastName: 'Safqi', PersonalNumber: '1244266242', EmployeeCode: 'EMP-0007',
  DateOfBirth: '2000-07-02', Gender: 'Mashkull', Email: 'art.safqi@example.com',
  Phone: '049 108 110', Address: 'Rr. Dervish Rozhaja 4', City: 'Prishtinë',
  HireDate: '2021-06-21', Position: 'Mbikëqyrës', Department: 'Operacione',
  Branch: 'Dega Prishtinë', Supervisor: 'Dorentina Qenaj', Active: 'Aktiv',
  ContractType: 'Part time', ContractStart: '2026-03-29', ContractEnd: '2027-03-28',
  Salary: '480', SalaryType: 'Neto', HourSalary: '3.20',
  Residency: 'I huaj', SecondJob: 'Jo', BankAccount: 'XK05 1212 0123 4567 8906', PensionNumber: '',
  ATKRegistered: 'Po', ATKRegisteredAt: '2021-06-25',
  WorkPermitNumber: 'LP-2026-118', WorkPermitIssued: '2026-01-10', WorkPermitExpires: '2027-01-09',
  HealthInsNumber: '', HealthInsPersonal: '', HealthInsExpires: '',
  AnnualLeave: '16', SickLeave: '19', LeaveYear: '2026',
  EmploymentEndDate: '', SeparationReason: '', Notes: '',
};

/** Çfarë pranon secili lloj kolone — një fjali, e njëjta te fleta e udhëzimeve. */
function acceptedText(c) {
  switch (c.type) {
    case 'date':       return 'Data: 2026-03-29, 29/03/2026 ose 29.03.2026';
    case 'gender':     return 'Femër / Mashkull / F / M';
    case 'bool':       return c.key === 'Active' ? 'Aktiv / Joaktiv (ose Po / Jo)' : 'Po / Jo (bosh = Jo)';
    case 'number':     return 'Numër, p.sh. 20 ose 19.5';
    case 'phone':      return 'Tekst — formatoje kolonën si TEKST që të mos humbë zeroja e parë';
    case 'lookup':     return 'Emri saktësisht si te sistemi (shih listat më poshtë)';
    case 'salaryType': return 'Bruto ose Neto — bosh lexohet si BRUTO';
    case 'residency':  return 'Rezident ose I huaj — bosh lexohet si Rezident';
    default:           return 'Tekst';
  }
}

/**
 * Shablloni `.xlsx` që shkarkon klienti: fleta e të dhënave me dy rreshta
 * shembull, plus një fletë udhëzimesh që i thotë emrat e sakta të
 * departamenteve dhe degëve që ekzistojnë tashmë.
 *
 * Emrat e departamenteve dalin nga sistemi, nuk shkruhen me dorë: pikërisht
 * mospërputhja e tyre («Operations» kundrejt «Operacione») është arsyeja e
 * zakonshme pse gjysma e rreshtave vjen pa departament.
 *
 * Koka është një rresht i VETËM edhe pse kolonat ndahen në seksione. Një
 * rresht i dytë me emrat e grupeve do të dukej më i rregullt, por leximi
 * mbrapsht e merr rreshtin e parë të mbushur si kokë — dhe fleta e vet nuk do
 * të rilexohej dot. Seksionet dallohen nga ngjyra dhe nga fleta e udhëzimeve.
 */
export function buildTemplateWorkbook({ departments = [], branches = [] } = {}) {
  const cols = IMPORT_COLUMNS;
  const HEAD = 2;

  const aoa = [
    ['Shablloni i importit të stafit — Nexuma'],
    ['Mos i ndrysho emrat e kolonave. Kolonat me yll janë të detyrueshme; të tjerat lëri bosh nëse s\'i ke. Fshij dy rreshtat shembull para se ta ngarkosh.'],
    cols.map((c) => (c.required ? `${c.label} *` : c.label)),
    cols.map((c) => c.example ?? ''),
    cols.map((c) => SECOND_EXAMPLE[c.key] ?? ''),
  ];

  const ws = XLSX.utils.aoa_to_sheet(aoa);
  ws['!cols'] = cols.map((c) => ({ wch: c.width || 16 }));
  ws['!freeze'] = { xSplit: 2, ySplit: HEAD + 1 };
  ws['!autofilter'] = { ref: XLSX.utils.encode_range({ s: { r: HEAD, c: 0 }, e: { r: HEAD, c: cols.length - 1 } }) };

  const at = (r, c) => ws[XLSX.utils.encode_cell({ r, c })];
  const style = (r, c, s) => { const cell = at(r, c); if (cell) cell.s = { ...(cell.s || {}), ...s }; };

  style(0, 0, { font: { bold: true, sz: 14, color: { rgb: HEX.navy } } });
  style(1, 0, { font: { sz: 9, italic: true, color: { rgb: HEX.muted } } });
  cols.forEach((c, i) => {
    style(HEAD, i, {
      font: { bold: true, sz: 9, color: { rgb: HEX.ink } },
      fill: { fgColor: { rgb: c.required ? HEX.req : SECTION_FILL[c.section] || HEX.soft } },
      border: line(HEX.rule),
      alignment: { horizontal: 'center', wrapText: true, vertical: 'center' },
    });
    style(HEAD + 1, i, { font: { sz: 9, italic: true, color: { rgb: HEX.muted } } });
    style(HEAD + 2, i, { font: { sz: 9, italic: true, color: { rgb: HEX.muted } } });
  });

  // ── Fleta e dytë: çfarë pranohet, dhe me çfarë emri ─────────────────────
  const names = (list) => (list || []).map((x) => x.Name || x.name).filter(Boolean);
  const bySection = SECTIONS
    .map((sec) => ({ sec, items: cols.filter((c) => c.section === sec.key) }))
    .filter((g) => g.items.length);

  const guide = [
    ['Si të plotësohet shablloni'],
    ['Kolonat janë të grupuara sipas kuptimit. Vetëm dy të parat janë të detyrueshme — çdo kolonë tjetër mund të mbetet bosh.'],
    [''],
    ['Kolona', 'A duhet?', 'Çfarë pranohet'],
  ];
  const guideSectionRows = [];
  bySection.forEach((g) => {
    guideSectionRows.push(guide.length);
    guide.push([g.sec.label.toUpperCase(), '', '']);
    g.items.forEach((c) => guide.push([
      c.label, c.required ? 'E detyrueshme' : 'Opsionale', acceptedText(c),
    ]));
  });

  guide.push(
    [''],
    ['Çfarë duhet ditur'],
    ['• «Qyteti» i bashkohet «Adresës» — te punëtori ruhet një adresë e vetme.'],
    ['• «Paga është bruto apo neto» ndryshon TËRË listën e pagave. Bosh lexohet si BRUTO — mos e lër bosh nëse i mban pagat neto.'],
    ['• «Rezidenca» dhe «Punësim i dytë» ndryshojnë tatimin. Bosh = «Rezident» dhe «Jo».'],
    ['• «Data e kontratës» krijon kontratën e parë; pa të, kontrata nuk krijohet fare (as paga, as afati).'],
    ['• «Data e mbarimit të kontratës» e bën kontratën me afat. Bosh = kontratë e pacaktuar.'],
    ['• «Pushimi vjetor» dhe «Pushimi mjekësor» janë ditët E MBETURA dhe hyjnë si bilanc hapës për vitin e dhënë.'],
    ['• «Kodi i punëtorit» bosh → sistemi e gjeneron vetë (EMP-00042). Kodi yt ruhet ashtu siç është.'],
    ['• Punëtorët e larguar: vëri «Statusi = Joaktiv» dhe plotëso «Data e largimit», përndryshe nuk dalin te raportet e qarkullimit.'],
    ['• Rreshtat me email, numër personal ose kod që ekziston tashmë nuk krijojnë punëtor të ri — sistemi nuk krijon dublikatë. Nëse rreshti ka ditë pushimi, përditësohet vetëm bilanci i pushimeve të atij punëtori.'],
    ['• Fotografia, kualifikimi dhe gjendja martesore NUK importohen: sistemi nuk i ruan ato fusha.'],
    ['• Llogaria e qasjes (login) nuk krijohet nga importi — bëhet nga «Menaxhimi i përdoruesve» pas importit.'],
    [''],
    ['Departamentet ekzistuese', ...(names(departments).length ? [] : ['(asnjë ende)'])],
    ...names(departments).map((n) => ['', n]),
    [''],
    ['Degët ekzistuese', ...(names(branches).length ? [] : ['(asnjë ende)'])],
    ...names(branches).map((n) => ['', n]),
  );

  const gs = XLSX.utils.aoa_to_sheet(guide);
  gs['!cols'] = [{ wch: 30 }, { wch: 16 }, { wch: 74 }];
  const gStyle = (r, c, st) => { const cell = gs[XLSX.utils.encode_cell({ r, c })]; if (cell) cell.s = { ...(cell.s || {}), ...st }; };
  gStyle(0, 0, { font: { bold: true, sz: 14, color: { rgb: HEX.navy } } });
  gStyle(1, 0, { font: { sz: 9, italic: true, color: { rgb: HEX.muted } } });
  [0, 1, 2].forEach((c) => gStyle(3, c, {
    font: { bold: true, sz: 9, color: { rgb: HEX.ink } },
    fill: { fgColor: { rgb: HEX.soft } },
    border: line(HEX.brand, 'bottom', 'medium'),
  }));
  guideSectionRows.forEach((r) => gStyle(r, 0, {
    font: { bold: true, sz: 9, color: { rgb: HEX.brand } },
    border: line(HEX.rule),
  }));

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Stafi');
  XLSX.utils.book_append_sheet(wb, gs, 'Udhëzime');
  return wb;
}

export function downloadTemplate(ctx) {
  XLSX.writeFile(buildTemplateWorkbook(ctx), 'nexuma-import-stafi.xlsx');
}

/**
 * Raporti i asaj që NUK hyri — rreshti origjinal plus arsyeja, që klienti ta
 * rregullojë në vend dhe ta ngarkojë sërish vetëm atë. Pa këtë, «5 nga 120
 * dështuan» është një numër pa asnjë rrugë përpara.
 */
export function buildErrorWorkbook(rows, headers) {
  const bad = rows.filter((r) => !r.ok || r.warnings.length);
  const aoa = [
    ['Rreshti', 'Statusi', 'Arsyeja', ...(headers || [])],
    ...bad.map((r) => [
      r.index + 2,
      r.ok ? 'U importua me vërejtje' : 'NUK u importua',
      [...r.errors, ...r.warnings].map((e) => e.message).join(' · '),
      ...(r.raw || []).map((c) => (c instanceof Date ? parseDateCell(c).value : c)),
    ]),
  ];
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  ws['!cols'] = [{ wch: 8 }, { wch: 20 }, { wch: 60 }, ...(headers || []).map(() => ({ wch: 16 }))];
  [0, 1, 2].forEach((c) => {
    const cell = ws[XLSX.utils.encode_cell({ r: 0, c })];
    if (cell) cell.s = { font: { bold: true, sz: 9 }, fill: { fgColor: { rgb: HEX.soft } }, border: line(HEX.rule) };
  });
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Rreshtat me problem');
  return wb;
}

export function downloadErrorReport(rows, headers) {
  XLSX.writeFile(buildErrorWorkbook(rows, headers), 'nexuma-import-gabimet.xlsx');
}
